// lib/clients/kostaboda.ts
// Client configuration for kostaboda.gr — Greek luxury tableware, glassware and
// home decoration. Onboarded 2026-08-26 via digital4u (the agency that also
// brought us acalight).
//
// NOT a single-brand shop: 43 manufacturers (Kosta Boda, Vista Alegre, Robbe &
// Berking, Royal Copenhagen, Costa Nova, Orrefors, Bugatti, Cutipol…) across
// 2,502 products.
//
// ⚠️ Read alongside the FreeRide decisions for this client — several values here
// are deliberate and look wrong without their evidence:
//   • 74836838 — why colour/material are MINED from text, not read from the feed
//   • 22c158f7 — why out-of-stock products are INDEXED, not hidden
//
// This file is the BIRTH source: `provision-client.ts` reads it once to create
// the Supabase row. After that Supabase is authoritative and this file is only
// the runtime fallback — never re-seed it to change live config.

import type { ClientConfig } from './types';

// ── Product Categories ──
// ⚠️ TOP-LEVEL ONLY. These must be exactly the values that appear in the index's
// `category` facet, because resolveCategories() matches the LLM's pick against
// that facet. Adding subcategory names here makes the agent choose values the
// resolver can't match, and the search returns 0 (tested and reverted on
// kaizershop 2026-06-04 — Search Setup Guide §6.1).
// RESYNCED 2026-09-14: digital4u restructured their category tree. «Σερβίτσια»
// became «Σερβίτσια Πιάτων», «Special Prices» became «Προσφορές», «Μπομπονιέρες»
// was folded into a new «Γάμος - Βάπτιση» shelf, and «Home Fragrances» was
// demoted to a sub-shelf of «Διακόσμηση». Eleven top levels became ten. Four of
// the names below were pointing at shelves that no longer exist, so the agent
// could steer a shopper into a department the resolver cannot match.
const PRODUCT_CATEGORIES = [
  'Σερβίτσια Πιάτων',
  'Αξεσουάρ Τραπεζιού',
  'Διακόσμηση',
  'Ποτήρια',
  'Μαχαιροπήρουνα',
  'Είδη Κουζίνας',
  'Είδη Μπαρ',
  // RENAMED 2026-10 by digital4u from «Γάμος - Βάπτιση», at our request: the old
  // name contained the feed's level separator (' - '), so «A - B - C» had two
  // readings. The slash removes the ambiguity at the source. The list is still
  // load-bearing for the CRAWL, not only for the agent prompt —
  // splitCategoryPaths anchors each path against it and warns on any path it
  // cannot anchor. See the parser.
  'Γάμος / Βάπτιση',
  // NEW 2026-10: seasonal shelf (Χριστουγεννιάτικο Τραπέζι / Διακόσμηση, 208
  // products). Seasonal shelves come and go — «Ανοιξιάτικο Τραπέζι» below did —
  // and verify-kostaboda-parse's drift check reports it when this one empties.
  'Εποχιακά Είδη',
  // «Ανοιξιάτικο Τραπέζι» REMOVED 2026-09-22: the seasonal shelf is gone
  // from the feed, and a prompt category with no products sends shoppers
  // nowhere — resolveCategories() silently DROPS what it cannot match, so this
  // fails quietly rather than loudly. Caught by verify-kostaboda-parse's
  // two-way drift check, which will report it again if they bring it back.
  'Προσφορές',
];

// ── Product Concepts ──
// Vocabulary the agent can use as search keywords. These are the words shoppers
// actually type for this catalogue, drawn from the real subcategory tree.
const PRODUCT_CONCEPTS = [
  // Tableware
  'πιάτα', 'πιάτο φαγητού', 'πιάτο γλυκού', 'πιάτο φρούτου', 'πιατέλα',
  'σερβίτσιο', 'μπωλ', 'σαλατιέρα', 'τουρτιέρα',
  // Glassware
  'ποτήρι', 'ποτήρι κρασιού', 'ποτήρι νερού', 'ποτήρι σαμπάνιας', 'ποτήρι ουίσκι',
  'σφηνάκι', 'καράφα', 'κανάτα', 'κρυστάλλινο ποτήρι',
  // Coffee & tea
  'φλυτζάνι', 'κούπα', 'τσαγιέρα', 'καφετιέρα', 'ζαχαριέρα', 'γαλατιέρα',
  // Cutlery & serving
  'μαχαιροπήρουνα', 'μαχαίρι', 'πιρούνι', 'κουτάλι', 'σετ σερβιρίσματος',
  'μαχαίρια τυριού', 'δίσκος σερβιρίσματος',
  // Kitchen
  'κατσαρόλα', 'τηγάνι', 'σκεύη μαγειρικής', 'εργαλεία κουζίνας', 'δοχείο τροφίμων',
  // Bar
  'παγοδοχείο', 'σαμπανιέρα', 'αξεσουάρ μπαρ', 'ανοιχτήρι',
  // Decoration
  'βάζο', 'κηροπήγιο', 'διακοσμητικό', 'κορνίζα', 'δίσκος',
  // Textiles & gifting
  'τραπεζομάντηλο', 'πετσέτα', 'μπομπονιέρα', 'δώρο γάμου',
];

function buildKostabodaPrompt(categories: string[], concepts: string[]): string {
  return `You are the digital sales assistant for kostaboda.gr, a Greek luxury tableware, glassware and home-decoration store. ALWAYS respond in Greek.

## HOW THE UI WORKS
The user sees a PRODUCT GRID next to the chat. The grid updates ONLY when you call searchProducts. If you don't call the tool, the user sees the SAME products — even if you talk about different ones.

Every searchProducts result gives you:
- "shown" — the first products of the grid, in the order the shopper sees them (title, brand, price, url, colour, material, availability…)
- "facets" — what the WHOLE result offers: brands, colours, materials, availability, with counts
- "priceRange", "cheapestItems" and "priciestItems"
- "onOffer" — the products in these results that are reduced right now ("count" and up to 5 "items" with old and new price). count 0 = nothing here is on offer.
Products you link as [title](url) appear in a row of picks above the grid. Link products ONLY when the shopper asked for a choice or a fact (see RESPONSES) — the grid already shows everything you found.

## YOUR JOB: UNDERSTAND THE INTENT, THEN SHOW THE RIGHT PRODUCTS
1. **Enough to search?** Search IMMEDIATELY when the message names any of:
   - a product type ("ποτήρια κρασιού", "σερβίτσιο", "βάζο", "σετ πιάτα", "μαχαιροπήρουνα")
   - a brand or collection ("Noritake", "Kosta Boda", "Ivory", "Bicos"). A BRAND always goes in the filters: «noritake» → searchTerm "Noritake", categories [], filters ["brand: Noritake"]. Without the filter, other brands' products fill the grid.
   - a WEDDING or BAPTISM gift, with or without a budget ("ψάχνω δώρο γάμου", "δώρο βάπτισης", "δώρο γάμου μέχρι 50€"). Search right away, then say in one sentence what the section offers (kinds of gift, price span) and ask ONE narrowing question (type or budget) with the options as SUGGESTIONS. Which search — the store's shelf «Γάμος / Βάπτιση» is split into sections, and a section is shown WHOLE with searchTerm "" (empty), keywords [], categories ["Γάμος / Βάπτιση"] and its filter:
     a. «δώρο γάμου» (for the couple) → the section filters ["subcategory: Δώρα Γάμου"] (vases, carafes, ice buckets, candlesticks, mostly over 150€), plus "price <= …" if a budget was given.
     b. «δώρο γάμου» with a budget UNDER 150€ («δώρο γάμου μέχρι 100€», «μέχρι 50€») → NOT the shelf (it has almost nothing that cheap): searchTerm "βάζο", keywords ["καράφα", "κηροπήγιο", "παγοδοχείο"], categories [], excludeCategories ["Γάμος / Βάπτιση"], filters ["price <= 100"].
     c. a baptism GIFT — «δώρο» together with βάπτιση / βάφτιση / βαφτίσια in ANY spelling, and no word μπομπονιέρα («δώρο βάπτισης», «ψάχνω δώρο για βάφτιση», «δώρο για βαφτίσια»): a gift FOR THE CHILD → NOT the shelf: searchTerm "παιδικό", keywords [], categories [], filters [] — the children's dinner sets and cutlery. The shelf's «Βάπτιση» section holds ONLY favours, so it is never the answer to a baptism gift.
     d. «δώρο για τον κουμπάρο» → the section filters ["subcategory: Δώρα Κουμπάρου"].
     e. only when the shopper SAYS μπομπονιέρες: «μπομπονιέρες γάμου» → filters ["subcategory: Μπομπονιέρες Γάμου"]; «μπομπονιέρες βάπτισης» → filters ["subcategory: Βάπτιση"].
     The ONLY sections are «Δώρα Γάμου», «Δώρα Κουμπάρου», «Μπομπονιέρες Γάμου» and «Βάπτιση» (there is no «Δώρα Βάπτισης»). A gift NEVER shows μπομπονιέρες (favours: small keepsakes the parents or the couple give the guests), and never recommend one as a gift.
   - another occasion WITH a budget or a product type ("δώρο για νέο σπίτι μέχρι 80€", "δώρο για φίλο καράφα").
   - a refinement of the last results ("σε λευκό", "από πορσελάνη", "μέχρι 100€", "μόνο Vista Alegre")
2. **Too vague?** (an occasion or a person with no product type or budget, other than weddings/baptisms: "δώρο για φίλο", "δώρο για τη μαμά", "κάτι για το σπίτι", "ιδέες") → do NOT search yet. Ask ONE short question with 3–5 concrete options drawn from this catalogue, and put the same options in the SUGGESTIONS tag so the shopper can tap one. Example: «Τι θα θέλατε να του χαρίσετε; Σερβίτσιο, ποτήρια, κάτι διακοσμητικό ή είδη μπαρ; Αν έχετε κάποιο ποσό στο μυαλό σας, πείτε μου.» [SUGGESTIONS: Σερβίτσια, Ποτήρια, Διακοσμητικά, Μέχρι 100€]
3. **The shopper answered or tapped an option** (messages like «Δείξε μου: Ποτήρια», «ποτήρια», «μέχρι 50») → search NOW, combining it with what they told you before (occasion, budget, the person's interests). Example: «δώρο για φίλο που του αρέσει να ψήνει» then «αξεσουάρ» → searchTerm "ψήσιμο", keywords ["μαχαίρι ψητού", "σανίδα κοπής", "σκεύος ψησίματος", "πιατέλα"] — the occasion shapes the search, not the generic word. Never ask two clarifying questions in a row: if something is still open, search with what you have and ask while showing results.
4. **NEVER ask permission to show products, and never end with an offer.** Never write «Θέλετε να σας δείξω;», «Θέλετε λεπτομέρειες;», «Αν θέλετε, μπορώ να ψάξω…». Products you found are ALREADY in the grid — describe them; the SUGGESTIONS chips are the next steps. If the shopper replies «ναι», «οκ», «δείξε μου», act at once with a search: if your last message offered choices (including an either/or like «μικρό ή εντυπωσιακό;»), take the FIRST option, or the most natural one for what you know (for a friend who likes cooking → "σκεύη μαγειρικής"; for a home → "διακόσμηση"). Never answer a yes with another question.
5. **When you search, call searchProducts FIRST and write your reply after the results** — no text before the call (it would show up as a second, half-finished message).

## WHEN TO CALL searchProducts
Whenever rule 1 or 3 above applies, and whenever the grid must change. Do NOT call it for greetings, thanks, general questions, or when you are asking the clarifying question of rule 2.

## REFINEMENTS — FROM WHAT IS DISPLAYED
- "Ποιο προτείνεις;", "ποιο είναι καλύτερο για δώρο;" → answer from "shown"; no new search. Link 1–3 products, each with its reason as FACTS from its fields: price, offer (old → new price), set size in its title, material, colour, brand — like «σετ 2 τεμαχίων, κρύσταλλο, 19€». Words like «κομψό», «μοντέρνο», «ποιοτικό», «εντυπωσιακό», «ιδανικό» are opinions no field backs: leave them out, and never claim packaging, quality or popularity.
- "Το πιο ακριβό;" / "το πιο φθηνό;" → the most expensive / cheapest of the PRODUCT TYPE being discussed, linked; no new search. "priciestItems" / "cheapestItems" cover every result, including other types (a salt shaker is not a «σετ πιάτων»): skip those, and if none there is that type, take it from "shown".
- "Πιο φθηνό" / "πιο οικονομικό" → if cheaper items exist in "shown" or "cheapestItems", recommend them (linked) without searching. If the current results have nothing cheaper, search the SAME product type with a price filter below the current prices.
- "Πιο ακριβό" / "κάτι πιο εντυπωσιακό" → same idea, upwards.
- "Μόνο Vista Alegre", "σε λευκό", "από κρύσταλλο" → check "facets": if it exists, search the SAME searchTerm/categories plus that filter (the grid must update). If "facets" shows it does not exist in these results, say so and offer what does exist.

## ⚠️ CALL searchProducts EXACTLY ONCE PER USER MESSAGE
The grid shows the results of your **LAST** searchProducts call, replacing whatever was there. So a second call does not add to the first — it REPLACES it.

Never re-run the search with a rephrased term to "refine" or "double-check". Measured failure: for «λευκά πιάτα από πορσελάνη» a first call found 14 correct plates, a second call with a reworded term found 1 unrelated cup — and the shopper saw the cup. One call, then describe what came back. If the result is poor, say so and ASK the customer to narrow it; do not silently search again.

## THIS STORE
A curated luxury tableware retailer carrying over 40 international brands — Kosta Boda, Vista Alegre, Robbe & Berking, Royal Copenhagen, Costa Nova, Orrefors, Bugatti, Cutipol, Le Jacquard Français and others. Prices range from everyday pieces around €10 to hand-made silver services costing tens of thousands of euros. Treat the customer as someone buying a gift or furnishing a home with care.

## CATEGORIES (use EXACTLY these values, they are the only ones that filter correctly)
${JSON.stringify(categories)}

## USEFUL SEARCH KEYWORDS
${JSON.stringify(concepts)}

## searchProducts PARAMETERS
- **searchTerm** (1–2 words): the product noun, collection or brand the shopper used — "ποτήρια κρασιού", "σερβίτσιο", "Ivory", "Noritake". NEVER only "δώρο", "κάτι", "ιδέες", "προϊόντα": for a gift, use the product type you are showing ("κηροπήγιο", "καράφα"), or "" (empty) for a whole section of the wedding/baptism shelf (rule 1).
- **categories** (1–3): only the categories that clearly match. Leave EMPTY for a brand or collection search — the brand filter or the name is precise, and extra categories flood the grid with other brands.
- **keywords** (2–4): product nouns that appear in titles ("πιάτο", "σερβίτσιο", "μπωλ"). Not adjectives on their own.
- **filters** — exactly this syntax:
  - price: "price <= 50", "price >= 100"
  - brand: "brand: Noritake" — ALWAYS use it when the shopper names a brand
  - colour: the colour the shopper SAID, as a noun — «μπλε» → "color: Μπλε", «λευκά» → "color: Λευκό". "Χρωματιστό" only when they ask for colourful / multicoloured.
  - material: "material: Πορσελάνη" · availability: "availability: Άμεσα διαθέσιμο"
  - subcategory: ONLY the four wedding/baptism sections in rule 1 — "Δώρα Γάμου", "Δώρα Κουμπάρου", "Μπομπονιέρες Γάμου", "Βάπτιση"
- **NEVER add a filter the shopper did not ask for** — no budget, colour or brand of your own invention.
- **Offers** («σε προσφορά έχετε;»): search the CURRENT product type with categories ["Προσφορές"] ONLY (not together with another category) and no price filter. Answer ONLY from "onOffer": name its items, linked, with both prices («από 120€ τώρα 89€»), and only those that are the product type the shopper asked about. If onOffer.count is 0, or none of its items are that product type, say it first, plainly («Σε σετ πιάτων δεν έχουμε προσφορές αυτή τη στιγμή.»); only then may you mention other reduced items, clearly as a different product type. NEVER call any other product "σε προσφορά".
- **Nothing found?** Say so in one sentence and put the closest alternatives in SUGGESTIONS (a wider budget, the same brand in another product type, without the category). No long apologies.

## FILTERABLE ATTRIBUTES
- category — one of the values above
- brand — e.g. "Kosta Boda", "Vista Alegre", "Robbe & Berking", "Cutipol", "Noritake"
- material (Υλικό) — noun form: "Πορσελάνη", "Κρύσταλλο", "Γυαλί", "Ανοξείδωτο ατσάλι", "Ασήμι", "Επάργυρο", "Ξύλο", "Stoneware", "Bone China", "Κεραμικό"
- color (Χρώμα) — noun form: "Λευκό" not "λευκά". Also "Χρωματιστό" for multicoloured pieces.
- availability (Διαθεσιμότητα) — exactly "Άμεσα διαθέσιμο" or "Διαθέσιμο από 7 έως 12 ημέρες"
- price — numeric, in euros

## IMPORTANT — HOW THIS CATALOGUE IS WRITTEN
1. **Product titles usually start with the COLLECTION name**, which is Latin even in Greek listings: "Abeille κανάτα 18 εκ.", "Blue Fluted Mega πιάτο σούπας", "Alt-Chippendale σετ 24 τεμαχίων". If the customer names a collection, search it as a keyword.
2. **The brand is usually NOT in the title.** "Abeille κανάτα" is made by La Rochere. Use the brand filter rather than expecting the brand in the product name.
3. **Some titles say only "σετ 24 τεμαχίων"** with no product type. These are cutlery services — rely on the category filter for them, not the title.
4. **Colour and material are only known for part of the catalogue.** If you filter by colour and get few results, try WITHOUT the colour filter and mention the options instead — do not tell the customer the item doesn't exist.
5. **"Διαθέσιμο από 7 έως 12 ημέρες" is not "unavailable".** It means the piece is not on the shelf right now and is available in 7 to 12 days — completely normal for silver and fine porcelain. Present it as a delivery time, never as a problem.
6. **Collection sets** carry a "price_note" and no price: their pieces are bought one by one on the set's page. Never quote a price for them or call them free.

## RESPONSES
- Warm, knowledgeable, concise: 2–4 sentences. This is a gift and home-furnishing purchase — speak like a good shop assistant.
- **After a search: describe, don't pick.** The grid already shows the products. In one or two sentences say what the results offer, from "priceRange" and "facets": the price span and the main materials, colours or brands — e.g. «Έχουμε ποτήρια κρασιού από 9€ έως 300€, κυρίως κρυστάλλινα, από Kosta Boda, Orrefors και Illum Crystal.» Then ask ONE question that narrows it (use, occasion, budget, style), phrased as a CHOICE between 2–3 options — «Για κόκκινο ή για λευκό κρασί;», «Καράφα, βάζο ή κηροπήγιο;» — never as a yes/no «Θέλετε να…;» (a «ναι» to that says nothing). Do NOT link or name single products here: the top of the grid is already "the first few", and naming them adds nothing.
- **Name products only when they ARE the answer — and then ALWAYS as links** (a product named without its link does not appear above the grid): the shopper asks for a choice or a fact («ποιο προτείνεις;», «το πιο φθηνό», «τι έχετε σε προσφορά», «ποιο είναι μπλε»), or searched for one specific product or collection. Then link 1–3 products [title](url), the title and url EXACTLY as in "shown" / "cheapestItems" / "priciestItems" / "onOffer", each with its factual reason (see REFINEMENTS). Facts come from the product's own fields: never call a product a brand, material, colour or offer that its "brand" / "material" / "color" / "original_price" does not show, and never claim what no field says (how many people a set serves, unless its title says so). If the shopper asked for a brand and "shown" has none of it, say so plainly.
- At most ONE short question per reply, and only when it really narrows the choice. The SUGGESTIONS chips carry the other next steps.
- Plain text and markdown only: NEVER HTML tags, images or tables. NEVER invent a link — no "/search?..." or category links; the only links are product urls from the results.
- Do not open with the total count ("Βρήκα 122 προϊόντα") — results include related items, so the number misleads. Open with what the results offer.
- Bullet points (-) for lists of more than 3 items.

## SUGGESTIONS
End every reply with [SUGGESTIONS: …] — 3 to 5 short options the shopper can tap to go on: product types, a budget ("Μέχρι 50€"), a colour or material, a brand that appears in "facets". When you asked a question (rule 2), the options ARE the answers. Each option 1–3 words.`;
}

export const kostabodaConfig: ClientConfig = {
  // ── Identity ──
  clientId: 'kostaboda',
  storeName: 'kostaboda.gr',
  storeDescription: 'a Greek luxury tableware, glassware & home decoration store',

  // ── Features ──
  features: {
    // Pure lexical. `semanticRatio: 0` below is what actually skips the OpenAI
    // embedder at crawl time (runCrawl.ts) — no vectors generated, no cost, and
    // parousiasi proves a Greek homeware catalogue ranks well without them.
    hybridSearch: false,
    // They explicitly asked for "έξυπνη αναζήτηση με chat ai".
    agentChat: true,
    // Enabled 2026-09-22, when they moved from our hosted demo to an embed on
    // their own site. It was OFF before for a structural reason, not caution:
    // on our demo page there IS no kostaboda cart to add to, so the button
    // could only have lied. On their own page their cart is right there.
    addToCart: true,
    externalPricing: false,
    // Heart wired to THEIR wishlist, like bazaar (founder, 2026-10-19). It was
    // off while ours was decorative: a second heart that saves nothing, next to
    // their real one, promised something we didn't deliver.
    // ⚠️ ON IN DEV ONLY until digital4u ship `wishlist.remove(id)` and the
    // `/account/wishlist/ids` list (as they did for bazaar) — see the ui.*
    // favourites keys below for why add-only is not safe on this theme.
    favorites: true,
  },

  // ── Embedding & Integration ──
  // Starts as a hosted demo page so we can see and tune it. The embed on
  // kostaboda.beta4u.gr is planned and is runbook step 7: it adds the origin to
  // allowed_origins via a DIRECT DB WRITE, not an edit here (the runtime reads
  // Supabase; this file is only the fallback). production_origins stays empty
  // until real go-live on the live domain — see step 8.
  allowedOrigins: [],
  productionOrigins: [],
  hostingStrategy: 'demo',
  // ── Cart ──
  // ⚠️ The runtime reads this from the Supabase `cart_config` column, so
  // changing this file alone does NOT change behaviour — the DB row must match.
  //
  // Verified against their live site 2026-09-22 before enabling:
  //   • `cart.add(product_id, quantity)` EXISTS in their
  //     catalog/view/javascript/common.js and posts to the standard
  //     `index.php?route=checkout/cart/add`.
  //   • It updates `#cart > button` and `#cart-total`, and BOTH ids exist in
  //     their header — so the header counter should refresh rather than sit
  //     stale, which was the main cosmetic risk with a custom theme.
  //
  // ⚠️ Their own buttons do NOT call `cart.add` — the theme does its own AJAX to
  // the same endpoint. So the function is present but unexercised on this
  // theme. That is the syfak shape, where a function existed, reported success
  // and added nothing (`f267501`). The mechanism here is far more likely to be
  // right, because it posts to the very endpoint their own buttons use — but
  // it still wants a browser proof on their site: add from our widget, then
  // reload THEIR cart page and confirm the line item is really there. Do not
  // accept our own «✓ Προστέθηκε» as evidence.
  //
  // If their own popup does not appear, set feedbackStrategy: 'toast' so we
  // show our own confirmation instead of trusting theirs.
  cart: {
    strategy: 'opencart_global',
    functionName: 'cart.add',
    flyToCartSelector: '#cart',
  },

  // ── Meilisearch ──
  indexName: 'products_kostaboda',
  embedderName: 'kostaboda-openai', // declared but unused while semanticRatio is 0
  locale: 'ell',

  // ── Feed ──
  // ⚠️ This is the BETA domain. Every product link and image URL in the feed
  // points here too, and the live domain does NOT serve this feed (404 as of
  // 2026-08-26). Go-live needs the live feed URL from digital4u.
  //
  // ✨ SWITCHED 2026-09-22 to digital4u's rebuilt export (feed shape 'store' —
  // see lib/crawler/parsers/skroutzXml.ts). The `?route=…` URL 301s to the
  // static file /s0_skroutz.xml; the redirect is followed, and the static file
  // is what makes their new "don't collide with a concurrent run" mechanism
  // work. What changed, all verified against the file:
  //   • prices are VAT-INCLUSIVE at last — kostaboda removed from
  //     FEED_PRICE_MULTIPLIER in the SAME change. These two never travel apart.
  //   • structured <color>/<material>/<collection> exist (49.9/37.2/38.1%)
  //   • <availability> is 2 clean values and agrees with <quantity> everywhere
  //   • category levels are joined by ' - ', not ' > ' — and «Γάμος - Βάπτιση»
  //     contains the separator, which is why splitCategoryPaths anchors on the
  //     systemPrompt.categories list below rather than splitting blindly.
  //
  // The OLD url (…/extension/feed/skroutz_xml) is STILL SERVED and still ships
  // ex-VAT prices and a different product count. Do not mix them.
  //
  // ✨ SWITCHED 2026-10-19 to the static file itself, on digital4u's
  // instruction («πλέον μπορείτε να κοιτάτε μόνο το …/s0_skroutz.xml όπου
  // ενημερώνεται κάθε ΧΧ:20»). The `?route=…` URL generated the file on every
  // request (~32s warm, >60s cold, 4 redirects); the static file is the same
  // bytes, regenerated by them hourly at :20, and downloads in a fraction of a
  // second. Same feed shape ('store'), so the parser is unchanged.
  //
  // 🚀 GO-LIVE 2026-10-30: the live site's copy of the same file. kostaboda.gr
  // is canonical now (www 301s to it), so no redirect hop. Same 2,309 products
  // and prices as the beta file; only the link/image domain differs.
  feedUrl: 'https://kostaboda.gr/s0_skroutz.xml',
  productLimit: 5000, // 2,960 on 2026-10-19 — headroom for growth
  feedFormat: 'skroutz_xml',

  // ── Dynamic Facet Fields ──
  // NOTE `xmlFilterKey: null` on colour and material: unlike parousiasi these do
  // NOT come from a feed <filters> element — there isn't one. The parser MINES
  // them from description text, titles and the category path. See decision 74836838.
  facetFields: [
    {
      field: 'color',
      label: 'Χρώμα',
      xmlFilterKey: null,
      isArray: true,
      // «καφε» = coffee OR brown, and this shop has an «Αξεσουάρ καφέ» category —
      // 67 of 104 «καφέ» titles are coffee vessels. «κρεμ» = cream the food vs
      // the colour. Project-wide convention, same as every other client.
      skipStems: new Set(['καφε', 'κρεμ']),
    },
    {
      field: 'material',
      label: 'Υλικό',
      xmlFilterKey: null,
      isArray: true,
      skipStems: new Set(),
    },
    {
      field: 'subcategory',
      label: 'Υποκατηγορία',
      xmlFilterKey: null,
      isArray: true,
    },
    {
      // Sidebar filter AND the card badge (ui.productBadge below reads this field).
      // Deliberately NOT used to hide products — see decision 22c158f7.
      field: 'availability',
      label: 'Διαθεσιμότητα',
      xmlFilterKey: null,
      isArray: true,
    },
  ],

  // ── Search Tuning ──
  // Starting values copied from parousiasi (nearest catalogue: Greek homeware,
  // similar size). ⚠️ NOT yet tuned against a real query battery for this client
  // — do that with a measured before/after set, the way syfak's thresholds were
  // (2026-08-25), rather than nudging them blind.
  search: {
    semanticRatio: 0.0, // pure lexical → crawl skips the OpenAI embedder entirely
    minScoreThreshold: 0.7,
    fallbackScoreThreshold: 0.80,
    maxResultsToFetch: 1000,
    maxResultsToReturn: 1000,
  },

  // ── Agent Search Tuning ──
  agentSearch: {
    minScoreThreshold: 0.8,
    layer4ScoreThreshold: 0.75,
    layer1: { limit: 100, matchingStrategy: 'last' },
    layer2: { maxCategories: 15, totalBudget: 80, minPerCategory: 12, matchingStrategy: 'last' },
    layer3: { limit: 20, maxKeywords: 5, minKeywordLength: 4, matchingStrategy: 'last' },
    layer4: { limit: 120, matchingStrategy: 'all' },
    discovery: { minTermLength: 4, matchingStrategy: 'last' },
    maxProductsToReturn: 200,
    // 2026-11-03: the prompt's "exactly once" rule alone did not hold on bazaar,
    // so it is enforced in code here too (route.ts prepareStep).
    oneSearchPerMessage: true,
    // The model reads the shopper's view of each result (first 40 products +
    // brand/colour/material counts + price stats), not 200 full records.
    compactToolOutput: true,
  },

  // ── Language ──
  language: {
    stopWords: [
      'ο', 'ι', 'το', 'τα', 'του', 'τις', 'τον',
      'κε', 'για', 'σε', 'με', 'απο', 'προς',
    ],
    // Deliberately EMPTY at birth. The Search Setup Guide §7.2 is explicit: only
    // add a synonym when the Greek word does NOT already appear in titles,
    // descriptions or categories — otherwise it just adds noise. This catalogue
    // is written in Greek throughout (πιάτα, ποτήρια, μαχαιροπήρουνα all appear
    // verbatim), so there is nothing to bridge on day one. Synonyms are
    // MERCHANT-owned: kostaboda adds them via the dashboard (draft → Publish)
    // once search tuning surfaces a real gap.
    synonyms: {},
    queryStopWords: [
      'από', 'απο', 'με', 'για', 'σε', 'και', 'στο', 'στη', 'στα',
      'στον', 'στην', 'του', 'της', 'των', 'τον', 'την', 'τους', 'τις',
      'ο', 'η', 'το', 'οι', 'τα', 'ένα', 'ενα', 'μια', 'μία', 'ένας', 'ενας', 'προς',
    ],
    // Latin words that must stay Latin when a query is transliterated from
    // Greeklish. The brand facet is added automatically (43 brands), but this
    // catalogue also puts a COLLECTION name at the front of 93% of its titles —
    // 393 distinct single-word names that are not brands. Without these,
    // "portofino piata" would transliterate "portofino" into nonsense Greek.
    // These are the highest-volume ones; extend as tuning surfaces more.
    greeklishKeepWords: [
      'flora', 'glamour', 'princess', 'bruk', 'portofino', 'colorplay', 'venezia',
      'cabbage', 'coralina', 'hollywood', 'dora', 'fiji', 'serena', 'giada',
      'chateau', 'transatlantica', 'darling', 'tivoli', 'volterra', 'calypso',
      'togo', 'alice', 'amazonia', 'bicos', 'contrast', 'em77', 'fantasy',
      'pearl', 'terrace', 'carat', 'circulon', 'claire', 'limelight',
      'mediterraneo', 'teak', 'abeille', 'aladdin', 'ornament',
    ],
    pricePatterns: {
      maxPrice: /(?:μέχρι|μεχρι|έως|εως|κάτω από|κατω απο|κατω|κάτω|up to|under|below)\s+(\d+(\.\d+)?)\s*(?:ευρω|ευρώ|€|euros?)/i,
      minPrice: /(?:πάνω από|πανω απο|πανω|πάνω|από|απο|over|above)\s+(\d+(\.\d+)?)\s*(?:ευρω|ευρώ|€|euros?)/i,
      range: /(?:από|απο|from)?\s*(\d+(?:\.\d+)?)\s*(?:-|–|έως|εως|μέχρι|μεχρι|to)\s*(\d+(?:\.\d+)?)\s*(?:ευρω|ευρώ|€|euros?)/i,
    },
    // «πλυντήριο πιάτων» appears in 725 descriptions. Measured: it makes a search
    // for «πιάτα» drag in 585 unrelated products (vs 516 genuine title matches);
    // stripping it drops that to 63. Widened from parousiasi's pattern to also
    // catch this feed's «πληντυρίου» misspelling (7 products).
    noisyPhrases: [
      /πλ[υη]ντ[ηήυ]ρ[ιί]?[ουαωνάύ]*\s+πι[αά]τ[ωώ]ν/gi,
    ],
  },

  // ── Index Settings (Bucket 2 — takes effect only on a re-crawl) ──
  // Mirrored in kostaboda.code.ts, which is what the DB-backed runtime merges in.
  indexSettings: {
    // ⚠️ color / material / availability are NOT here on purpose. Structured
    // attributes must be FILTERABLE-ONLY — making them searchable causes
    // cross-field noise (Search Setup Guide §3.1): a query for «πιάτα λευκά»
    // starts matching the colour field of every white product.
    searchableAttributes: [
      'title', 'title_normalized',
      'brand',
      'category', 'category_normalized',
      'subcategory', 'subcategory_normalized',
      'id', 'model',
      'description_short', 'description_short_normalized',
    ],
    // MUST stay ['*'] — this client has 4 facet fields, and enumerating the
    // list makes Meilisearch return only those per hit, which silently breaks
    // the agent-chat sidebar (bit kaizershop, fixed in b2a82f9).
    displayedAttributes: ['*'],
    rankingRules: [
      'words', 'typo', 'proximity', 'attribute', 'sort', 'exactness',
    ],
    // Greek stemmer on the RAW fields only. Never on the _normalized twins —
    // double-stemming makes them stop matching.
    localizedAttributes: [
      {
        locales: ['ell'],
        attributePatterns: ['title', 'category', 'subcategory', 'description_short'],
      },
    ],
    documentTemplate: '{{doc.category}} {{doc.subcategory}} - {{doc.brand}} {{doc.title}}. {{doc.color}} {{doc.material}}. {{doc.description}}',
    faceting: {
      maxValuesPerFacet: 500,
      sortFacetValuesBy: { '*': 'count' as const },
    },
    // Meilisearch's default 5/9, chosen deliberately rather than copied.
    // 98% of titles START with a short Latin collection name — Alta, Rib, Riva,
    // Fiji, Dora, Bruk. At oneTypo:4 those become mutually reachable by typo and
    // the wrong collection surfaces. 5/9 gives words under 5 chars NO typo
    // tolerance, which is exactly the protection those names need, while Greek
    // words are long enough to still benefit. Tune with a measured battery, the
    // way syfak's 6/10 was — never by copying another client's number.
    typoTolerance: {
      minWordSizeForTypos: { oneTypo: 5, twoTypos: 9 },
    },
  },

  // ── System Prompt ──
  systemPrompt: {
    categories: PRODUCT_CATEGORIES,
    productConcepts: PRODUCT_CONCEPTS,
    buildPrompt: buildKostabodaPrompt,
  },

  // ── UI Configuration ──
  ui: {
    // Muted gold, replacing black — founder's call 2026-09-14, deliberately
    // reversing the 2026-08-26 decision to mirror their monochrome theme
    // (#000 / #fff / #343434, Cardo + Gotham Greek, no accent colour).
    //
    // What forced the question: the search highlight is TEXT COLOUR ONLY
    // (.text-highlight sets `color` and nothing else), so a black brand colour
    // painted the matched word black on text that was already black. The
    // highlight fired correctly on every search and was perfectly invisible,
    // in the filter labels and the product titles alike.
    //
    // Gold echoes the gold rims running through their own catalogue, and reads
    // as an accent on a monochrome page rather than as a web-UI colour. It
    // clears WCAG AA both ways (~4.6:1 against white), so it is legible both as
    // highlighted text and as white-on-gold on the OPEN CHAT button.
    //
    // ✨ SUPERSEDED 2026-09-22 — GOLD REPLACED BY THEIR OWN TURQUOISE.
    //
    // The gold above was our inference from their catalogue's gold rims, chosen
    // when we had only the monochrome storefront to go on. Their actual brand
    // colour is the turquoise running through their site — the «Αγορά» button,
    // the cart icon, the cookie-banner accept button. Confirmed as a literal in
    // their own stylesheet, not eyeballed from a screenshot:
    //   catalog/view/theme/digital4u/dependencies/custom_stylesheet.css
    //   .news-form .newsletter_subtitle { color: #74CCD3; … }
    // #5FA4A9 is the darker tone from the same palette, used for hover.
    brandColor: '#74CCD3',
    brandColorHover: '#5FA4A9',
    // 🔴 AND THE OVERRIDE COMES BACK — for the OPPOSITE reason it was removed.
    //
    // It was deleted on 2026-09-14 because it held #000000, identical to body
    // text, so every matched word was painted black on black and the highlight
    // was perfectly invisible for three weeks. The lesson recorded then was
    // "a redundant override holding the same value as the brand colour is how
    // this hides" — which is still true. This is not that.
    //
    // Their turquoise is genuinely UNREADABLE as text: **1.86:1 against white**,
    // where WCAG AA wants 4.5:1. Inheriting it would reproduce the same
    // invisible-highlight bug by a different route — too pale instead of too
    // dark. Measured, not assumed:
    //   #74CCD3  1.86:1   their brand turquoise      ← unusable as text
    //   #5FA4A9  2.85:1   their darker tone          ← still unusable
    //   #2B8188  4.57:1   same hue (184°), darkened  ← passes
    //
    // So the brand colour stays exactly theirs everywhere it is a SURFACE (the
    // OPEN CHAT button, accents), and only the matched-word text uses the
    // darkened teal. Same hue, so it still reads as their colour.
    //
    // This is the kaizershop pattern, and for the identical reason: its brand
    // cyan is too pale to read as text, so it overrides highlightColor too.
    highlightColor: '#2B8188',
    // Text and icons drawn ON the turquoise — the OPEN CHAT button, add-to-cart,
    // chat bubbles. White is the platform default and is WRONG here: white on
    // #74CCD3 is **1.86:1**, black is **11.3:1**.
    //
    // Black rather than an invented dark teal, because it is what THEY do:
    //   custom_stylesheet.css
    //   .pagination li.active > span { background: #b6e1e5 !important; color: black; }
    // Their «Αγορά» button uses white on turquoise, so their own site is
    // inconsistent — we follow the readable half of it.
    brandTextColor: '#000000',
    // The 6px scrollbar on the RESULTS screen and the filters sidebar.
    //
    // ✅ THEIR OWN TOKEN, not a shade we picked. digital4u publish their design
    // system as CSS custom properties in custom_stylesheet.css:
    //
    //     html { --accent_1: #f28c22;  --accent_2: #3C3C3C;  --accent2: #5FA4A9;
    //            --accent_3: #74CCD3;  --accent_4: #233645;  --accent_5: #fff3e7;
    //            --accent_1_amea: #6D5117; }
    //
    // `--accent_2` (#3C3C3C, rgb 60/60/60) is the dark grey their own buttons
    // use — founder confirmed with a screenshot of their «Αναζήτηση» button,
    // dark grey with white text, beside a turquoise heading.
    //
    // At 11.03:1 against white it is the first scrollbar value here that clears
    // the 3:1 a UI element wants, by a wide margin. The three before it did not:
    //     #74CCD3  1.86:1  their brand (--accent_3)   washed out
    //     #5FA4A9  2.85:1  their mid teal (--accent2)  still faint
    //     #2B8188  4.57:1  OUR derived colour          readable but not theirs
    // This is both theirs AND readable, which none of the others managed.
    //
    // ℹ️ Worth knowing: `--accent_1_amea` is their ACCESSIBILITY variant of the
    // orange (7.40:1 vs 2.46:1) — digital4u already derive darker versions of
    // pale brand colours for legibility, which is exactly what `highlightColor`
    // does here. Same instinct, arrived at independently.
    scrollbarColor: '#3C3C3C',
    productTitleColor: '#000000',
    // ── Chat entry + way back (2026-11-03) ──
    // Unset, the button and the back link fall back to the deliberately English
    // «OPEN CHAT» / «CLOSE CHAT» (SearchBar.tsx, ChatHeader.tsx): each client sets
    // its own words. Same wording as kaizershop; formal «σας», like the greeting.
    chatOpenLabel: 'Ρωτήστε τον AI βοηθό',
    chatBackLabel: 'Επιστροφή στην αναζήτηση',
    chatBackTone: 'brand',
    // The assistant describes and narrows after a search and names products
    // only when asked for a choice (founder, 2026-11-03: the automatic 2–3
    // picks were mostly the grid's own first row). No placeholders, then.
    chatPicksOnRequest: true,
    // ── Favourites — same keys as bazaar (2026-10-19) ──
    // Their common.js `wishlist.add(id)` is a TOGGLE keyed on THEIR page: if
    // their own button for that product has class `wishlisted` it REMOVES
    // (GET …account/wishlist&remove=id) instead of adding. Add-only from our
    // modal could therefore remove a saved product while we say «Προστέθηκε».
    // With the ids list our heart knows the real state, so we only call add
    // for an unsaved product (their button can't be `wishlisted` then).
    favoritesFunction: 'wishlist.add',
    // Their `wishlist.remove` is EMPTY today (`remove: function () {}`) and
    // `/account/wishlist/ids` is a 404 — both requested from digital4u, same
    // as they built for bazaar.
    favoritesRemoveFunction: 'wishlist.remove',
    favoritesIdsUrl: '/account/wishlist/ids',
    // Their add inserts `.alert.alert-success.alert-dismissible` (same theme
    // as bazaar) — remove it after OUR add so it doesn't sit behind the modal.
    favoritesHideShopNotice: '.alert-dismissible',
    // Heart only for logged-in shoppers: guests get their «login» prompt back
    // as `success`, which would make our «Προστέθηκε» a lie. Guest pages have
    // 0 logout links (checked 2026-10-19); the logged-in form is unverified —
    // bazaar's beta wrote the short /account/logout, hence `*=`.
    favoritesLoggedInSelector: 'a[href*="account/logout"]',
    // No favoritesColor: their own saved heart is the brand turquoise
    // (`--accent_3`, custom_stylesheet.css), which is the default.
    // Their own OpenCart placeholder, put back deliberately. The parser now
    // nulls this URL out of `image_url` (see realImageUrl) so code can tell a
    // real photo from a missing one — 327 of 2,591 products have none. Setting
    // it here means ORDINARY cards still render exactly the graphic they render
    // today; only the checks that ask "is there a real photo?" change behaviour.
    fallbackImageUrl: 'https://kostaboda.gr/image/no_image.png',
    fontCssVariable: '--font-gotham-greek',
    // Availability badge on the card. Reads the `availability` facet field
    // (first value). Muted, non-alarming tones: «Διαθέσιμο από 7 έως 12
    // ημέρες» is a delivery promise, NOT a warning.
    //
    // ⚠️ THE IN-STOCK VALUE IS DELIBERATELY ABSENT. 2,303 of 2,790 products
    // (82.5%) are immediately available, so badging them says nothing — it just
    // puts a sticker on almost every card and trains the eye to ignore badges
    // entirely. Badge the EXCEPTION, not the norm. ProductCard renders a badge
    // only when the value has an entry here (`if (value && colors)`), so
    // omitting the key is all it takes — an in-stock card simply shows none.
    //
    // ⚠️ THESE KEYS ARE THE FEED'S OWN SENTENCES, MATCHED EXACTLY. Since the
    // 2026-09-22 feed switch the parser passes <availability> through verbatim
    // instead of bucketing it (shape 'store' — see shopperAvailability), so if
    // digital4u reword the sentence the badge silently stops rendering. That is
    // the quiet-failure shape this project keeps getting bitten by, so
    // verify-kostaboda-parse.ts now checks these keys against the live feed.
    //
    // The pre-2026-09-22 keys were «Κατόπιν παραγγελίας» / «Μέχρι
    // εξαντλήσεως», kept here only so an old index is still readable.
    productBadge: {
      field: 'availability',
      values: {
        'Διαθέσιμο από 7 έως 12 ημέρες': { background: '#6b7280' },
        'Κατόπιν παραγγελίας': { background: '#6b7280' },
        'Μέχρι εξαντλήσεως': { background: '#b45309' },
      },
    },
    // All 11 top-level categories are legitimate merchandising shelves here —
    // nothing to hide at birth. Revisit if seasonal shelves («Ανοιξιάτικο
    // Τραπέζι») go stale, the way kaizershop's Πασχαλινά did.
    categoriesToHide: new Set<string>(),
    exampleQueries: [
      { emoji: '🥂', text: 'Ποτήρια σαμπάνιας για γιορτή', description: 'Celebration glassware' },
      { emoji: '🎁', text: 'Δώρο γάμου μέχρι 100 ευρώ', description: 'Wedding gift' },
      { emoji: '🍽️', text: 'Λευκό σερβίτσιο από πορσελάνη', description: 'Dinner service' },
      { emoji: '🕯️', text: 'Διακοσμητικά για το σαλόνι', description: 'Home decoration' },
      { emoji: '🍴', text: 'Ασημένια μαχαιροπήρουνα', description: 'Silver cutlery' },
    ],
    // Starting merchant content — the shop edits these in the dashboard.
    // Chosen from the real catalogue: the highest-volume brands and categories.
    topSearches: ['ποτήρια', 'σερβίτσια', 'μαχαιροπήρουνα', 'vista alegre', 'βάζα'],
    strings: {
      searchPlaceholder: [
        'Δώρο γάμου μέχρι 100€',
        'Ποτήρια κρασιού κρυστάλλινα',
        'Λευκό σερβίτσιο πορσελάνης',
        'Ασημένια μαχαιροπήρουνα',
      ],
      brandLabel: 'Brand',
      filtersTitle: 'Φίλτρα',
      suggestedCategories: 'Προτεινόμενες Κατηγορίες',
      otherCategories: 'Άλλες Κατηγορίες',
      priceRange: 'Εύρος Τιμής',
      productsTitle: 'Προϊόντα',
      noResults: 'Δε βρέθηκαν προϊόντα',
      noResultsHint: 'Δοκιμάστε διαφορετικές λέξεις - κλειδιά ή πιο γενικούς όρους αναζήτησης',
      showLess: 'Λιγότερα',
      showMore: (n: number) => `+${n} ακόμα...`,
      popularProducts: 'Δημοφιλή Προϊόντα',
      searchingSuggestions: 'Αναζήτηση Προτάσεων',
      searchingProducts: 'Αναζήτηση προϊόντων...',
      addToCart: 'ΠΡΟΣΘΗΚΗ ΣΤΟ ΚΑΛΑΘΙ',
      topSearchesTitle: 'Top Αναζητήσεις',
      aiAssistantTitle: 'AI Βοηθός',
      aiAssistantGreeting: 'Πώς μπορώ να σας βοηθήσω; Ρωτήστε με για προϊόντα, δώρα ή ιδέες για το τραπέζι σας.',
    },
  },
};
